import React, { useState, useCallback } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Icon from '~/components/icons/Icon';
import ImagePlaceholder from '~/components/ImagePlaceholder';
import ProductAvailability from '~/components/product/ProductAvailability';
import { useDropcart } from '~/context/DropcartContext';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { useOrder } from '~/context/OrderContext';
import { pushAddToCart, pushSelectItem, type ListContext } from '~/gtm';
import { useRoutePath } from '~/hooks/useRoutePath';
import { ApiProductVariant, Product } from '~/types/Product';
import { getMainProductImage, getProductImageSrcSet } from '~/utils/images';
import { useFormatPrice } from '~/utils/price';

// ProductCard is shared across grids with different column counts below 992px
// (CategoryPage `xs=6 md=3`, SearchPage/BrandDetail `xs=6 md=4 lg=3`, homepage Embla
// sliders at 20-50% depending on breakpoint) and can't see its container's context, so
// this is the widest (safe-to-over-request) superset across all of them rather than a
// value tuned to one grid: full ~50vw column below 768px, ~33vw (the widest sub-992
// tablet column, from SearchPage's md=4) between 768-991px, fixed 250px from 992px up.
const PRODUCT_CARD_SIZES = '(max-width: 767px) 50vw, (max-width: 991px) 33vw, 250px';

interface ProductCardProps {
  product: Product;
  lazyloaded?: boolean;
  listContext?: ListContext;
  index?: number;
  onProductRef?: (_element: HTMLElement | null, _product: Product, _index: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, lazyloaded = true, listContext, index, onProductRef }) => {
  const { t } = useTranslation();
  const variant: ApiProductVariant | undefined = product.defaultVariantData ?? undefined;
  const routePath = useRoutePath();
  const { addToCart } = useOrder();
  const { openDropcart } = useDropcart();
  const { addMessage } = useFlashMessages();
  const formatPrice = useFormatPrice();
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);

  // Ref callback for IntersectionObserver (view_item_list tracking)
  const cardRef = useCallback(
    (element: HTMLElement | null) => {
      if (onProductRef && index !== undefined) {
        onProductRef(element, product, index);
      }
    },
    [onProductRef, product, index]
  );

  // Handle product click for select_item tracking
  const handleProductClick = useCallback(() => {
    if (listContext && variant?.currency) {
      pushSelectItem(product, listContext, variant.currency, index);
    }
  }, [product, listContext, variant?.currency, index]);

  // Configurable products require choices on the detail page, so they don't get
  // a direct add-to-cart button — they show a "Konfigurátor" flag instead.
  // The listing/search endpoint only exposes the lightweight `customizable` boolean.
  const isCustomizable = product.customizable === true;

  // Product is simple (non-variant) if it has no options or only one variant
  const isSimpleProduct = !product.options || product.options.length === 0 || product.variants.length <= 1;

  const handleAddToCart = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = (variant as { '@id'?: string })?.['@id'];
      const variantCode = variantId?.split('/').pop();

      if (!variantCode) return;

      setIsAddToCartLoading(true);
      try {
        await addToCart(variantCode, 1);
        // GTM: Push add_to_cart event after successful API call
        if (variant?.currency) {
          pushAddToCart(product, variant, 1, variant.currency, listContext);
        }
        openDropcart();
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('add_to_cart_error');
        console.error(err);
        addMessage('error', errorMessage);
      } finally {
        setIsAddToCartLoading(false);
      }
    },
    [variant, addToCart, openDropcart, addMessage, t, product, listContext]
  );

  const mainImage = getMainProductImage(product.images, 'small_thumbnail');
  const image = mainImage?.thumbnails?.medium ?? mainImage?.path;
  const imageSrcSet = getProductImageSrcSet(mainImage);
  const name = product.name;

  return (
    <div ref={cardRef} className={`product-miniature${isAddToCartLoading ? ' product-miniature--loading' : ''}`}>
      <Link
        to={routePath('product', product.slug)}
        className="product-miniature__link"
        aria-label={name || 'Product Link'}
        onClick={handleProductClick}
      />
      <div className="product-miniature__image-container">
        {(isCustomizable || (product?.productLabels && product.productLabels.length > 0)) && (
          <div className="product-miniature__badges">
            {isCustomizable && (
              <Badge pill className="product-miniature__badge product-miniature__badge--configurator">
                {t('configurator')}
              </Badge>
            )}
            {product?.productLabels?.map((label) => (
              <Badge
                pill
                className="product-miniature__badge"
                style={{ backgroundColor: label.color, ...(label.textColor ? { color: label.textColor } : {}) }}
                key={label.id}
              >
                {label.iconPath && (
                  <img
                    src={label.iconPath}
                    alt={`${label.name} icon`}
                    className="me-1"
                    decoding="async"
                    width={14}
                    height={14}
                  />
                )}
                {label.name}
              </Badge>
            ))}
          </div>
        )}
        {image ? (
          <img
            src={image}
            srcSet={imageSrcSet}
            sizes={imageSrcSet ? PRODUCT_CARD_SIZES : undefined}
            alt={name}
            className="product-miniature__image img-fluid"
            loading={lazyloaded ? 'lazy' : 'eager'}
            fetchpriority={lazyloaded ? 'low' : 'high'}
            decoding="async"
            width={250}
            height={250}
          />
        ) : (
          <ImagePlaceholder className="product-miniature__image img-fluid" />
        )}
        <div className="product-miniature__action">
          {!isCustomizable && isSimpleProduct && variant?.inStock ? (
            <Button
              variant="primary"
              className="product-miniature__button"
              onClick={handleAddToCart}
              disabled={isAddToCartLoading}
              aria-label={t('add_product_to_cart', { name })}
            >
              <Icon name="bag5" size={24} className="d-block d-md-none" />
              <span className="d-none d-md-inline-block">
                {t('add_to_cart')}
                {isAddToCartLoading && <Spinner animation="border" size="sm" className="ms-2" />}
              </span>
            </Button>
          ) : (
            <Link
              to={routePath('product', product.slug)}
              className="btn btn-brand product-miniature__button"
              aria-label={t('common.detail', { name })}
            >
              <Icon name="eye" size={24} className="d-block d-md-none" />
              <span className="d-none d-md-inline-block">{t('common.detail')}</span>
            </Link>
          )}
        </div>
      </div>
      <p className="h3 product-miniature__name">{name}</p>
      {/* Configurable products don't expose availability — hide it entirely. */}
      {!isCustomizable && (
        <ProductAvailability
          // Card availability, derived from the product-level flags:
          //  - not available         → "Není skladem"
          //  - available & isStock   → "Skladem"
          //  - available & !isStock  → stock category label (or a generic "Skladem" when none is set)
          isAvailable={product.isAvailable ?? false}
          variant="icon"
          size="small"
          className="product-miniature__availability"
          stockCategory={
            (product.isAvailable ?? false) && !(product.isStock ?? false) ? variant?.stockCategory : undefined
          }
        />
      )}
      <div className="product-miniature__price">
        {/* Configurable products only quote a starting price — the configurator adds to it. */}
        {isCustomizable && <span className="product-miniature__price-from">{t('components.product.price_from')}</span>}
        {!!variant?.originalPrice && variant?.originalPrice > variant?.price && (
          <span className="product-miniature__price--previous">
            {formatPrice(variant.originalPrice, variant.currency)}
          </span>
        )}
        <strong className="product-miniature__price--current">
          {variant?.price ? (
            <>{formatPrice(variant.price, variant.currency)}</>
          ) : (
            <>{formatPrice(0, variant?.currency)}</>
          )}
        </strong>
      </div>
    </div>
  );
};

export default ProductCard;
